import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useBackgroundTrack } from '../../context/MusicContext';
import { advanceReveal } from '../../services/firebase/games';
import { useWebRTC } from '../../context/WebRTCContext';
import { CHATTERS_ID } from '../../constants/game';
import { logError } from '../../utils/logger';
import PlayerVideoCard, { KickDialog } from '../../components/PlayerVideoCard';

const TARGET_RATIO = 16 / 9;
const GAP = 8; // px — matches gap-2

function bestGridLayout(count, containerW, containerH) {
  if (count === 0) return { cols: 1, rows: 1 };
  let bestCols = 1, bestRows = count, bestScore = Infinity;
  for (let c = 1; c <= count; c++) {
    const r = Math.ceil(count / c);
    const cellW = (containerW - (c - 1) * GAP) / c;
    const cellH = (containerH - (r - 1) * GAP) / r;
    if (cellW <= 0 || cellH <= 0) continue;
    const score = Math.abs(cellW / cellH - TARGET_RATIO);
    if (score < bestScore) { bestScore = score; bestCols = c; bestRows = r; }
  }
  return { cols: bestCols, rows: bestRows };
}

export default function RevealingPhase({ game, round, scores, isHost, playerAvatars, isChatter = false, displayMode = false }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const webrtc = useWebRTC();
  useBackgroundTrack('reveal');

  const {
    predRevealOrder = [],
    scoreRevealOrder = [],
    revealPhase = 'predictions',
    revealIndex = 0,
    predictionTargetIndex = 0,
  } = round;

  const currentOrder = revealPhase === 'predictions' ? predRevealOrder : scoreRevealOrder;
  const currentPredictorUid = predRevealOrder[revealIndex];
  const currentScorerUid = scoreRevealOrder[revealIndex];
  const currentUid = currentOrder[revealIndex];
  const isChattersSlot = currentUid === CHATTERS_ID;

  // Prediction targets = all playerIds except the current predictor
  const predictionTargets = game.playerIds.filter((id) => id !== currentPredictorUid);
  const currentTargetUid = predictionTargets[predictionTargetIndex];
  const isCurrentTargetChatters = currentTargetUid === CHATTERS_ID;

  const isMyTurn = user.uid === currentUid || (isChattersSlot && isHost);
  const currentName = isChattersSlot ? 'Chatters' : (game.playerNames?.[currentUid] || 'Unknown');

  const myScores = scores[currentUid] || {};

  // Already-revealed data for the RevealProgress section
  const donePredictions =
    revealPhase === 'scores'
      ? predRevealOrder
      : predRevealOrder.slice(0, revealIndex);

  const doneScores =
    revealPhase === 'scores' ? scoreRevealOrder.slice(0, revealIndex) : [];

  // Whether WebRTC is available (regular online players only)
  const hasWebRTC = !!webrtc && !game.inPersonMode;

  // Kick dialog
  const [kickConfirmUid, setKickConfirmUid] = useState(null);
  const [kicking, setKicking] = useState(false);

  // Grid layout — derived from ResizeObserver (same pattern as SongSelectionPhase)
  const gridContainerRef = useRef(null);
  const [gridLayout, setGridLayout] = useState({ cols: 2, rows: 2, rowHeight: 120, cardW: 213, cardH: 120 });

  // Players shown in the small grid for the current phase
  const currentGridPlayerIds = useMemo(() => {
    if (revealPhase === 'predictions') {
      const predictor = predRevealOrder[revealIndex];
      const target = (game.playerIds.filter((id) => id !== predictor))[predictionTargetIndex ?? 0];
      return game.playerIds.filter((uid) => uid !== predictor && uid !== target && uid !== CHATTERS_ID);
    }
    const scorer = scoreRevealOrder[revealIndex];
    return game.playerIds.filter((uid) => uid !== scorer && uid !== CHATTERS_ID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealPhase, revealIndex, predictionTargetIndex, game.playerIds.join(','), predRevealOrder.join(','), scoreRevealOrder.join(',')]);

  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el || !hasWebRTC || currentGridPlayerIds.length === 0) return;
    const compute = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (h <= 0) return;
      const { cols, rows } = bestGridLayout(currentGridPlayerIds.length, w, h);
      const cellW = (w - (cols - 1) * GAP) / cols;
      const cellH = (h - (rows - 1) * GAP) / rows;
      let cardW = cellW, cardH = cellW / TARGET_RATIO;
      if (cardH > cellH) { cardH = cellH; cardW = cellH * TARGET_RATIO; }
      setGridLayout({ cols, rows, rowHeight: Math.floor(cellH), cardW: Math.floor(cardW), cardH: Math.floor(cardH) });
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasWebRTC, currentGridPlayerIds.length]);

  async function handleKick(uid) {
    setKicking(true);
    try {
      await webrtc?.kickPlayer?.(uid);
    } catch (err) {
      logError(err);
    } finally {
      setKicking(false);
      setKickConfirmUid(null);
    }
  }

  async function handleAdvance() {
    try {
      await advanceReveal(game.id, round.id, round, game.playerIds);
    } catch (err) {
      logError(err);
    }
  }

  // ── Shared: WebRTC card props helper ────────────────────────────────────────
  function webrtcProps(uid) {
    if (!hasWebRTC) return { isViewerChatter: isChatter };
    const isMe = uid === user.uid;
    const isChatSlot = uid === CHATTERS_ID;
    return {
      isViewerChatter: isChatter,
      mediaState: webrtc.playerMediaStates?.[uid],
      localCameraStream: isMe ? webrtc.localCameraStream : null,
      remoteStreamsByUid: !isMe ? webrtc.remoteStreamsByUid?.get(uid) : null,
      cameraStreamId: webrtc.playerMediaStates?.[uid]?.cameraStreamId,
      myCameraEnabled: isMe ? webrtc.cameraEnabled : undefined,
      myMicEnabled: isMe ? webrtc.micEnabled : undefined,
      toggleCamera: isMe ? webrtc.toggleCamera : null,
      toggleMic: isMe ? webrtc.toggleMic : null,
      isMuted: !isMe && !isChatSlot ? (webrtc.localMutes?.[uid] ?? false) : false,
      volume: !isMe && !isChatSlot ? (webrtc.localVolumes?.[uid] ?? 1) : 1,
      isHidden: !isMe && !isChatSlot ? (webrtc.localHidden?.[uid] ?? false) : false,
      onMuteToggle: !isMe && !isChatSlot ? () => (webrtc.localMutes?.[uid] ? webrtc.unmuteRemote?.(uid) : webrtc.muteRemote?.(uid)) : null,
      onVolumeChange: !isMe && !isChatSlot ? (v) => webrtc.setRemoteVolume?.(uid, v) : null,
      onHideToggle: !isMe && !isChatSlot ? () => (webrtc.localHidden?.[uid] ? webrtc.showRemoteCamera?.(uid) : webrtc.hideRemoteCamera?.(uid)) : null,
      onKick: isHost && !isMe && !isChatSlot ? () => setKickConfirmUid(uid) : null,
    };
  }

  // ── Predictions phase ────────────────────────────────────────────────────────
  if (revealPhase === 'predictions') {
    const predictor = currentPredictorUid;
    const target = currentTargetUid;
    const predictorName = isChattersSlot ? 'Chatters' : (game.playerNames?.[predictor] || 'Unknown');
    const targetName = isCurrentTargetChatters ? 'Chatters' : (game.playerNames?.[target] || 'Unknown');
    const predictionValue = myScores.predictions?.[target];

    // Remaining players (not predictor, not current target) shown in small scroll row
    const smallGridPlayers = game.playerIds.filter(
      (uid) => uid !== predictor && uid !== target
    );

    return (
      <div className="flex-1 flex flex-col min-h-0 overflow-y-auto px-3 py-3 gap-3">

        {/* ── Featured pair: predictor (left) + target (right, with button below) ── */}
        {hasWebRTC ? (
          <div className="grid grid-cols-2 gap-2 items-start flex-none">
            {/* Left column: predictor card */}
            <PlayerVideoCard
              uid={predictor}
              name={predictorName}
              avatarEmoji={playerAvatars?.[predictor]}
              isMe={user.uid === predictor}
              isChattersSlot={isChattersSlot}
              highlight={isMyTurn}
              {...webrtcProps(predictor)}
              renderTopLeft={() => (
                <p className="t-text text-xs font-semibold leading-tight drop-shadow truncate">
                  {predictorName}
                </p>
              )}
            />

            {/* Right column: target card + advance button (centred on the subject of the reveal) */}
            <div className="flex flex-col gap-1.5">
              <PlayerVideoCard
                uid={target}
                name={targetName}
                avatarEmoji={playerAvatars?.[target]}
                isMe={user.uid === target}
                isChattersSlot={isCurrentTargetChatters}
                highlight={true}
                {...webrtcProps(target)}
                renderTopLeft={() => (
                  <p className="t-text text-xs font-semibold leading-tight drop-shadow truncate">
                    {targetName}
                  </p>
                )}
                renderTopRight={() =>
                  !displayMode && isMyTurn && predictionValue != null ? (
                    <span className="t-text font-bold text-lg drop-shadow leading-tight">
                      {predictionValue}
                    </span>
                  ) : null
                }
              />
              {isMyTurn && !displayMode && (
                <>
                  <button
                    onClick={handleAdvance}
                    className="w-full t-btn-primary font-semibold rounded-lg px-3 py-2 text-sm transition-colors t-focus-ring"
                  >
                    {isChattersSlot
                      ? t(user.uid === target ? 'revealing.chatPredictedRevealButtonSelf' : 'revealing.chatPredictedRevealButton', { name: targetName, score: predictionValue ?? '?' })
                      : t('revealing.iPredictedRevealButton', { name: targetName, score: predictionValue ?? '?' })}
                  </button>
                  <p className="text-xs t-text-disabled text-center leading-snug">
                      {t('revealing.round1HintPrediction')}
                    </p>
                </>
              )}
              {(!isMyTurn || displayMode) && (
                <p className="text-xs t-text-disabled text-center">
                  {t('revealing.waitingForPredictor', { name: currentName })}
                </p>
              )}
            </div>
          </div>
        ) : (
          /* ── In-person / no WebRTC: text-based featured pair ─────────────── */
          <div className="t-bg-surface t-border-accent border-2 rounded-2xl p-5 space-y-3 shrink-0 w-fit mx-auto">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold t-text">{predictorName}</span>
              <span className="t-text-disabled">→</span>
              <span className="font-semibold t-text">{targetName}</span>
              {isMyTurn && (
                <span className="ml-auto text-xs t-bg-accent-muted t-text px-2 py-0.5 rounded-full shrink-0">
                  {isChattersSlot ? t('revealing.revealAsHost') : t('revealing.yourTurn')}
                </span>
              )}
            </div>
            {isMyTurn && !displayMode && predictionValue != null && (
              <p className="text-5xl font-bold t-text text-center">{predictionValue}</p>
            )}
            {isMyTurn && !displayMode && (
              <>
                <button
                  onClick={handleAdvance}
                  className="block mx-auto t-btn-primary font-semibold rounded-lg px-4 py-3 transition-colors t-focus-ring"
                >
                  {isChattersSlot
                    ? t(user.uid === target ? 'revealing.chatPredictedRevealButtonSelf' : 'revealing.chatPredictedRevealButton', { name: targetName, score: predictionValue ?? '?' })
                    : t('revealing.iPredictedRevealButton', { name: targetName, score: predictionValue ?? '?' })}
                </button>
                <p className="text-xs t-text-disabled text-center leading-snug max-w-xs mx-auto">
                  {t('revealing.round1HintPrediction')}
                </p>
              </>
            )}
            {(!isMyTurn || displayMode) && (
              <p className="text-sm t-text-disabled text-center">
                {t('revealing.waitingForPredictor', { name: currentName })}
              </p>
            )}
          </div>
        )}

        {/* ── Dynamic grid: remaining players ──────────────────────────────── */}
        {hasWebRTC && smallGridPlayers.length > 0 && (
          <div
            ref={gridContainerRef}
            className="flex-1 min-h-30 overflow-hidden px-0 grid gap-2"
            style={{ gridTemplateColumns: `repeat(${gridLayout.cols}, 1fr)` }}
          >
            {smallGridPlayers.filter((uid) => uid !== CHATTERS_ID).map((uid) => {
              const pName = game.playerNames?.[uid] || uid;
              return (
                <div
                  key={uid}
                  className="relative flex items-center justify-center overflow-hidden"
                  style={{ height: gridLayout.rowHeight }}
                >
                  <div style={{ width: gridLayout.cardW, height: gridLayout.cardH }} className="shrink-0">
                    <PlayerVideoCard
                      uid={uid}
                      name={pName}
                      avatarEmoji={playerAvatars?.[uid]}
                      isMe={uid === user.uid}
                      stretch
                      className="w-full h-full"
                      {...webrtcProps(uid)}
                      renderTopLeft={() => (
                        <p className="t-text text-xs font-medium leading-tight drop-shadow truncate">
                          {pName}
                        </p>
                      )}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Predictions revealed section ────────────────────────────────── */}
        {donePredictions.length > 0 && (
          <div className="flex-none overflow-y-auto">
            <RevealProgress
              donePredictions={donePredictions}
              doneScores={doneScores}
              game={game}
              scores={scores}
            />
          </div>
        )}

        {/* Kick confirmation dialog */}
        {kickConfirmUid && (
          <KickDialog
            name={game.playerNames?.[kickConfirmUid] || kickConfirmUid}
            onConfirm={() => handleKick(kickConfirmUid)}
            onCancel={() => setKickConfirmUid(null)}
            kicking={kicking}
          />
        )}
      </div>
    );
  }

  // ── Scores phase ─────────────────────────────────────────────────────────────
  const scorerName = isChattersSlot ? 'Chatters' : (game.playerNames?.[currentScorerUid] || 'Unknown');
  const scorerScores = scores[currentScorerUid] || {};
  const isScorer = user.uid === currentScorerUid || (isChattersSlot && isHost);

  const otherScorerPlayers = game.playerIds.filter((uid) => uid !== currentScorerUid);

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-y-auto px-3 py-3 gap-3">

      {/* ── Featured scorer ──────────────────────────────────────────────── */}
      {hasWebRTC ? (
        <div className="flex flex-col gap-1.5 max-w-sm mx-auto w-full flex-none">
          <PlayerVideoCard
            uid={currentScorerUid}
            name={scorerName}
            avatarEmoji={playerAvatars?.[currentScorerUid]}
            isMe={user.uid === currentScorerUid}
            isChattersSlot={isChattersSlot}
            highlight={true}
            {...webrtcProps(currentScorerUid)}
            renderTopLeft={() => (
              <p className="t-text text-xs font-semibold leading-tight drop-shadow truncate">
                {scorerName}
              </p>
            )}
            renderTopRight={() =>
              !displayMode && isScorer && scorerScores.actualScore != null ? (
                <span className="t-text font-bold text-xl drop-shadow leading-tight">
                  {scorerScores.actualScore}
                </span>
              ) : null
            }
          />
          {isScorer && !displayMode && (
            <>
              <button
                onClick={handleAdvance}
                className="w-full t-btn-primary font-semibold rounded-lg px-3 py-2 text-sm transition-colors t-focus-ring"
              >
                {isChattersSlot
                  ? t('revealing.chatActuallyRatedButton', { score: scorerScores.actualScore ?? '?' })
                  : t('revealing.iActuallyRatedButton', { score: scorerScores.actualScore ?? '?' })}
              </button>
              <p className="text-xs t-text-disabled text-center leading-snug max-w-xs mx-auto">
                {t('revealing.round1HintScore')}
              </p>
            </>
          )}
          {(!isScorer || displayMode) && (
            <p className="text-sm t-text-disabled text-center">
              {t('revealing.waitingForScore', { name: scorerName })}
            </p>
          )}
        </div>
      ) : (
        /* In-person score reveal */
        <div className="t-bg-surface t-border-accent border-2 rounded-2xl p-6 space-y-3 text-center flex-none w-fit mx-auto">
          <p className="font-semibold t-text text-lg">{scorerName}</p>
          {isScorer && !displayMode ? (
            <>
              {isChattersSlot && scorerScores.chatterCount != null && (
                <p className="text-xs t-text-warning">
                  {t('revealing.averageOfChatters', { count: scorerScores.chatterCount })}
                </p>
              )}
              <p className="text-sm t-text-muted">
                {isChattersSlot ? t('revealing.chattersAverageScore') : t('revealing.iActuallyRatedThis')}
              </p>
              <p className="text-6xl font-bold t-text">
                {scorerScores.actualScore ?? '?'}
              </p>
              <button
                onClick={handleAdvance}
                className="block mx-auto t-btn-primary font-semibold rounded-lg px-4 py-3 transition-colors t-focus-ring"
              >
                {isChattersSlot
                  ? t('revealing.chatActuallyRatedButton', { score: scorerScores.actualScore ?? '?' })
                  : t('revealing.iActuallyRatedButton', { score: scorerScores.actualScore ?? '?' })}
              </button>
              <p className="text-xs t-text-disabled text-center leading-snug max-w-xs mx-auto">
                {t('revealing.round1HintScore')}
              </p>
            </>
          ) : (
            <p className="text-sm t-text-muted">
              {t('revealing.waitingForScore', { name: scorerName })}
            </p>
          )}
        </div>
      )}

      {/* Predictions for the current scorer */}
      <div className="flex justify-center flex-none">
      <div className="t-bg-surface t-border border rounded-2xl p-4 space-y-2 w-fit min-w-48 max-w-full">
        <p className="text-xs font-medium t-text-disabled uppercase tracking-widest">
          {t('revealing.predictions')}
        </p>
        <ul className="space-y-1.5">
          {game.playerIds
            .filter((uid) => uid !== currentScorerUid)
            .map((uid) => {
              const predicted = scores[uid]?.predictions?.[currentScorerUid];
              const isExact =
                isScorer &&
                scorerScores.actualScore != null &&
                predicted === scorerScores.actualScore;
              const predictorName =
                uid === CHATTERS_ID ? 'Chatters' : (game.playerNames?.[uid] || uid);
              let predictionLabel;
              if (uid === user.uid) {
                predictionLabel = t('revealing.youThoughtWouldRate', { target: scorerName });
              } else if (currentScorerUid === user.uid) {
                predictionLabel = t('revealing.thoughtYouWouldRate', { name: predictorName });
              } else {
                predictionLabel = t('revealing.thoughtWouldRate', { name: predictorName, target: scorerName });
              }
              return (
                <li key={uid} className="t-bg-input rounded-lg px-3 py-2 text-sm t-text-muted text-center">
                  {predictionLabel}{' '}
                  <span className="t-text-secondary font-bold">{predicted ?? '?'}</span>
                  {isExact && (
                    <span className="t-text-success font-semibold ml-1">— {t('revealing.exact')}</span>
                  )}
                </li>
              );
            })}
        </ul>
      </div>
      </div>

      {/* ── Dynamic grid: other players ───────────────────────────────────── */}
      {hasWebRTC && otherScorerPlayers.filter((uid) => uid !== CHATTERS_ID).length > 0 && (
        <div
          ref={gridContainerRef}
          className="flex-1 min-h-30 overflow-hidden grid gap-2"
          style={{ gridTemplateColumns: `repeat(${gridLayout.cols}, 1fr)` }}
        >
          {otherScorerPlayers.filter((uid) => uid !== CHATTERS_ID).map((uid) => {
            const pName = game.playerNames?.[uid] || uid;
            return (
              <div
                key={uid}
                className="relative flex items-center justify-center overflow-hidden"
                style={{ height: gridLayout.rowHeight }}
              >
                <div style={{ width: gridLayout.cardW, height: gridLayout.cardH }} className="shrink-0">
                  <PlayerVideoCard
                    uid={uid}
                    name={pName}
                    avatarEmoji={playerAvatars?.[uid]}
                    isMe={uid === user.uid}
                    stretch
                    className="w-full h-full"
                    {...webrtcProps(uid)}
                    renderTopLeft={() => (
                      <p className="t-text text-xs font-medium leading-tight drop-shadow truncate">
                        {pName}
                      </p>
                    )}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Reveal progress ───────────────────────────────────────────────── */}
      {(donePredictions.length > 0 || doneScores.length > 0) && (
        <div className="flex-none overflow-y-auto">
          <RevealProgress
            donePredictions={donePredictions}
            doneScores={doneScores}
            game={game}
            scores={scores}
          />
        </div>
      )}

      {/* Kick confirmation dialog */}
      {kickConfirmUid && (
        <KickDialog
          name={game.playerNames?.[kickConfirmUid] || kickConfirmUid}
          onConfirm={() => handleKick(kickConfirmUid)}
          onCancel={() => setKickConfirmUid(null)}
          kicking={kicking}
        />
      )}
    </div>
  );
}

// ── Predictions / scores revealed summary ─────────────────────────────────────
function RevealProgress({ donePredictions, doneScores, game, scores }) {
  const { t } = useTranslation();

  function displayName(uid) {
    return uid === CHATTERS_ID ? 'Chatters' : (game.playerNames?.[uid] || uid);
  }

  return (
    <div className="flex justify-center">
    <div className="t-bg-surface t-border border rounded-2xl p-4 space-y-4 shrink-0 w-fit min-w-48 max-w-full">
      {doneScores.length > 0 && (
        <div>
          <h4 className="text-xs font-medium t-text-disabled uppercase tracking-widest mb-2">
            {t('revealing.scoresRevealed')}
          </h4>
          <div className="space-y-1.5">
            {doneScores.map((uid) => (
              <div key={uid} className="flex items-center justify-between">
                <span className="t-text-secondary text-sm">{displayName(uid)}</span>
                <span className="font-bold t-text">
                  {scores[uid]?.actualScore != null ? `${scores[uid].actualScore} / 10` : '- / 10'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {donePredictions.length > 0 && (
        <div>
          <h4 className="text-xs font-medium t-text-disabled uppercase tracking-widest mb-3">
            {t('revealing.predictionsRevealed')}
          </h4>
          <div className="space-y-2">
            {donePredictions.map((uid) => {
              const playerScore = scores[uid] || {};
              const others = game.playerIds.filter((id) => id !== uid);
              return (
                <div key={uid} className="t-bg-input rounded-lg px-3 py-2.5">
                  <p className="text-xs font-medium t-text-muted mb-1.5">
                    {t('revealing.predicted', { name: displayName(uid) })}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {others.map((targetId) => {
                      const predicted = playerScore.predictions?.[targetId];
                      const targetRevealed = doneScores.includes(targetId);
                      const isExact =
                        targetRevealed &&
                        predicted != null &&
                        predicted === scores[targetId]?.actualScore;
                      return (
                        <span key={targetId} className="text-xs">
                          <span className="t-text-disabled">{displayName(targetId)}:</span>{' '}
                          <span className={isExact ? 't-text-success font-bold' : 'font-bold t-text'}>
                            {predicted ?? '?'}
                          </span>
                          {isExact && (
                            <span className="t-text-success font-semibold"> — {t('revealing.exact')}</span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
